import React from 'react';
import { DomainValuation } from '@/types';
import {
  formatValuationRange,
  getConfidenceColor,
  getValuationTier,
  isValidValuation
} from '@/lib/valuation-utils';
import { Badge } from '@/components/ui/badge';

interface ValuationBadgeProps {
  valuation?: DomainValuation;
  className?: string;
  showConfidence?: boolean;
  compact?: boolean;
}

const ValuationBadge: React.FC<ValuationBadgeProps> = ({
  valuation,
  className = '',
  showConfidence = false,
  compact = false
}) => {

  if (!isValidValuation(valuation)) {
    return (
      <span className={`text-gray-400 dark:text-gray-500 text-xs ${className}`}>
        N/A
      </span>
    );
  }

  const { color, label } = getValuationTier(valuation!);
  const rangeText = formatValuationRange(valuation!);
  const confidenceColor = getConfidenceColor(valuation!.confidence);

  if (compact) {
    return (
      <div className={`flex items-center space-x-1 ${className}`}>
        <span className={`text-xs font-medium ${color}`}>
          {rangeText}
        </span>
        {showConfidence && (
          <Badge
            variant="outline"
            className={`text-xs px-1.5 py-0.5 ${confidenceColor}`}
          >
            {valuation!.confidence.charAt(0).toUpperCase()}
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col space-y-1 ${className}`}>
      <div className="flex items-center space-x-2">
        <span className={`text-sm font-semibold ${color}`}>
          {rangeText}
        </span>
        <Badge
          variant="outline"
          className={`text-xs px-2 py-1 ${confidenceColor}`}
        >
          {label}
        </Badge>
      </div>
      {showConfidence && (
        <div className="flex items-center space-x-1">
          <div className={`w-2 h-2 rounded-full ${confidenceColor.split(' ')[0]}`} />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {valuation!.confidence} confidence
          </span>
        </div>
      )}
    </div>
  );
};

export default ValuationBadge;
